import { Component, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs';

import { PageSize } from 'src/app/enumeration/pageSize';
import { CustomerApiService } from 'src/app/service/customer/customer-api.service';
import { ThemeService } from 'src/app/service/theme/theme.service';

// Explicit type-safe interface schemas layout definitions
export interface UnverifiedCustomerResponse {
  nextContinuation: number
  pageTotal: number
  allTotal: number
  unverifiedCustomerInfoRespLst: UnverifiedCustomerInfoRespLst[]
}

export interface UnverifiedCustomerInfoRespLst {
  srNo: number
  loginPhone: string
  profileName: boolean
  nrcFrontPhoto: boolean
  nrcBackPhoto: boolean
  nrcNumber: boolean
  unverifiedNote: string
  unverifiedBy: string
  unverifiedTS: string
}

@Component({
  selector: 'app-customer-unverify-list',
  standalone: true,
  imports: [
    CommonModule, // Required for formatting pipes and conditional template expressions
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule
  ],
  templateUrl: './customer-unverify-list.component.html',
  styleUrl: './customer-unverify-list.component.css',
})
export class CustomerUnverifyListComponent implements OnInit {
  // Functional constructor-less dependency injection engine token primitives
  private readonly router = inject(Router);
  private readonly apiService = inject(CustomerApiService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  // Link global layout dark state signal instantly to view parameters matrix
  readonly isDarkMode = this.themeService.isDarkMode;

  // Fully refactored component view data tracking metrics utilizing signals
  readonly unverifiedList = signal<UnverifiedCustomerInfoRespLst[]>([]);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1); // Optimized 1-based index template pagination matching parameter
  readonly pageSize = signal<number>(PageSize.CUSTOMER_UNVERIFY_LIST);

  // Layout Loading Indicators
  readonly loading = signal<boolean>(false);
  mathMin = Math.min; // Exposed directly as helper utility for footer total count calculations

  ngOnInit(): void {
    this.GetCustomerUnverifiedData();
  }

  /**
   * Dispatches network lookup streaming requests to load records matching active page counts
   */
  GetCustomerUnverifiedData(): void {
    this.loading.set(true);
    const zeroBasedPage = this.page() - 1; // Translate 1-based page signal back down to server requirements

    this.apiService.getCustomerUnverifiedReasonAll(zeroBasedPage)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)) // Unconditionally drops active background loading processing widgets
      )
      .subscribe({
        next: (v: UnverifiedCustomerResponse) => {
          this.unverifiedList.set(v?.unverifiedCustomerInfoRespLst || []);
          this.allTotal.set(v?.allTotal || 0);
        },
        error: (err: any) => {
          console.error('Failed to download unverified user matrix listings:', err);
          alert(err?.message || err || 'Network error encountered while downloading profiles.');
        }
      });
  }

  /**
   * Captures event page shifts step sequences securely across viewport boundaries
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event); // Direct 1-based assignment perfectly matching ngx-pagination elements
      this.GetCustomerUnverifiedData();
    }
  }

  /**
   * Safe action router navigation parameter utilities
   */
  goToProfile(customerPhone: string): void {
    if (!customerPhone) return;
    this.router.navigate(['nav/customer-info/', customerPhone.trim()]);
  }
}
